// Display-only customer-name masking. Nothing is mutated server-side — these
// helpers only transform what is SHOWN in the UI when an org has the
// `mask_customer_names` flag enabled (per-account, reusable). Used to hide
// management-company / customer names (e.g. "Lindy", "Bayshore") in filenames
// and customer/property name fields across Intake Hub, Accounts, Viewer, etc.
import { useEffect, useState } from 'react';
import api from './api';

// Strip the leading company-name token from a bill filename, keeping the
// vendor + a short bill id. e.g.
//   "LindyPropertyManagementCompany_Peco_BillIda5c91f29-2f07-...pdf"
//     -> "Peco_Bill_a5c91f29.pdf"
export function maskFilename(filename) {
  if (!filename || typeof filename !== 'string') return filename;
  const m = filename.match(/^(.+?)_(.+?)_BillId([A-Za-z0-9-]+)\.(\w+)$/i);
  if (m) {
    const vendor = m[2];
    const id = (m[3] || '').replace(/-/g, '').slice(0, 8);
    return `${vendor}_Bill_${id}.${m[4]}`;
  }
  const dot = filename.lastIndexOf('.');
  const ext = dot >= 0 ? filename.slice(dot) : '';
  const base = dot >= 0 ? filename.slice(0, dot) : filename;
  const parts = base.split('_');
  // "{Company}_{Vendor}_{...}" → drop the company prefix.
  if (parts.length >= 3) return parts.slice(1).join('_') + ext;
  return filename; // UUID-only / unrecognised → leave as-is
}

function hash4(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h).toString(36).toUpperCase().padStart(4, '0').slice(0, 4);
}

// Replace a customer/property/company name with a stable, non-identifying alias
// (same input always → same alias, so grouping/ordering stays meaningful).
export function maskName(name) {
  if (name == null || name === '' || name === '—' || name === '-') return name;
  return `Customer ${hash4(String(name).trim().toLowerCase())}`;
}

// Mask a service/property address with a stable alias.
export function maskAddress(addr) {
  if (addr == null || addr === '' || addr === '—' || addr === '-') return addr;
  return `Address ${hash4(String(addr).trim().toLowerCase())}`;
}

// Module-level cache so we fetch the flags once per account.
const _cache = {};

export function usePrivacy() {
  let accId = null;
  try {
    const raw = localStorage.getItem('current_account');
    accId = raw ? JSON.parse(raw)?.id : null;
  } catch { /* ignore */ }

  const [flags, setFlags] = useState(
    accId && _cache[accId] ? _cache[accId] : { names: false, addresses: false }
  );

  useEffect(() => {
    if (!accId) return;
    if (_cache[accId] !== undefined) { setFlags(_cache[accId]); return; }
    let active = true;
    api.get(`/accounts/${accId}/ui-settings`)
      .then(({ data }) => {
        _cache[accId] = {
          names: !!data?.mask_customer_names,
          addresses: !!data?.mask_service_addresses,
        };
        if (active) setFlags(_cache[accId]);
      })
      .catch(() => { _cache[accId] = { names: false, addresses: false }; });
    return () => { active = false; };
  }, [accId]);

  return {
    maskNames: flags.names,
    maskAddresses: flags.addresses,
    fileName: (f) => (flags.names ? maskFilename(f) : f),
    name: (n) => (flags.names ? maskName(n) : n),
    address: (a) => (flags.addresses ? maskAddress(a) : a),
  };
}
